import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  COLOR_SELECTABLE_ASSET,
  COLOR_NON_MAPPED_CHECKED,
  COLOR_MAPPED_CHECKED,
  COLOR_GROUPED_PARTIAL_DARK_PINK,
  COLOR_SCROLLABLE,
  IPS_ASSET_ID_PROP,
} from '../utils/ViewerConstants';
import { log, getLeafDbIds, extractConductorName } from '../utils/Viewer';

// ─────────────────────────────────────────────────────────────
// useViewerModel
//
// Encapsulates all APS viewer state, refs, attribute loading,
// color application, and selection handling so the main
// component only manages form/checkbox state and render.
//
// Parameters:
//   preEFormMappings        — original (parent-owned) mappings
//   highlightIdList         — ids passed in as props
//   checkedAssetIds         — live checked state from main component
//   autoCheckOnViewerSelection
//   saveVisibleTextRef      — useLatestRef of saveVisibleText
// ─────────────────────────────────────────────────────────────
const useViewerModel = ({
  preEFormMappings,
  highlightIdList,
  checkedAssetIds,
  autoCheckOnViewerSelection,
  saveVisibleTextRef,
}) => {

  // ── Viewer instance ──────────────────────────────────────────
  const viewerRef = useRef(null);

  // ── Attribute data refs ──────────────────────────────────────
  const allAttributesRef          = useRef([]);
  const assetIdToDbIdRef          = useRef(new Map());
  const dbIdToAssetIdsRef         = useRef(new Map()); // Map<dbId, Set<assetId>>
  const assetIdToNameRef          = useRef(new Map());
  const assetIdToCalloutNumberRef = useRef(new Map());
  const validDbIdsRef             = useRef(new Set());
  const goldDbIdsRef              = useRef(new Set());

  // ── Derived state ────────────────────────────────────────────
  const [goldAssetIds,   setGoldAssetIds]   = useState(new Set());
  const [mappedAssetIds, setMappedAssetIds] = useState(new Set());

  // Ref mirror of mappedAssetIds so callbacks with empty dep
  // arrays can always read the latest value without going stale.
  const mappedAssetIdsRef = useRef(new Set());

  // ── Stale-closure guards ─────────────────────────────────────
  const loadAndApplyAllAttributesRef = useRef(null);
  const isUpdatingFromViewerRef      = useRef(false);

  // ── Viewer selection state (drives auto-check effect) ────────
  const [viewerSelectedDbIds, setViewerSelectedDbIds] = useState([]);

  // ─────────────────────────────────────────────────────────────
  // resolveCheckedDbIds — kept for internal use (fitToView, select)
  // ─────────────────────────────────────────────────────────────
  const resolveCheckedDbIds = useCallback((assetIds) => {
    const dbIds = new Set();
    assetIds.forEach((id) => {
      const dbId = assetIdToDbIdRef.current.get(id);
      if (dbId !== undefined) dbIds.add(dbId);
    });
    return dbIds;
  }, []);

  // ─────────────────────────────────────────────────────────────
  // resolveDbId
  // ─────────────────────────────────────────────────────────────
  const resolveDbId = useCallback(
    (assetIdStr) => assetIdToDbIdRef.current.get(assetIdStr),
    [],
  );

  // ─────────────────────────────────────────────────────────────
  // getAssetIdsFromDbId — O(1) reverse lookup via dbIdToAssetIdsRef.
  // Returns all asset IDs mapped to a given dbId (Set<string>).
  // Replaces the former getAssetIdFromDbId linear scan and
  // correctly handles grouped attributes (multiple IPS IDs per node).
  // ─────────────────────────────────────────────────────────────
  const getAssetIdsFromDbId = useCallback(
    (dbId) => dbIdToAssetIdsRef.current.get(dbId) ?? new Set(),
    [],
  );

  // ─────────────────────────────────────────────────────────────
  // hasPartialGroups — true when any dbId has more than one asset
  // ID and at least one (but not all) of them is currently checked.
  // Drives the orange legend entry and grouped badge in ViewerLegend.
  // ─────────────────────────────────────────────────────────────
  const hasPartialGroups = useMemo(() => {
    const checkedSet = new Set(checkedAssetIds);
    for (const [, assetIds] of dbIdToAssetIdsRef.current.entries()) {
      if (assetIds.size <= 1) continue;
      const checkedCount = [...assetIds].filter((id) => checkedSet.has(id)).length;
      if (checkedCount > 0 && checkedCount < assetIds.size) return true;
    }
    return false;
  }, [checkedAssetIds]);

  // ─────────────────────────────────────────────────────────────
  // applyIdListColors
  // Accepts checkedAssetIdSet (Set<string>) instead of checkedDbIds
  // so per-asset-ID visibility inside grouped attributes is preserved.
  //
  // Color priority (highest wins, applied last):
  //   1. Gold   — unchecked selectable assets
  //   2. Orange — grouped attribute with some (not all) IDs checked
  //   3. Cyan   — all checked, non-mapped
  //   4. Purple — all checked, mapped (highest priority)
  // ─────────────────────────────────────────────────────────────
  const applyIdListColors = useCallback((
    viewer,
    attributeResults,
    idList,
    checkedAssetIdSet = new Set(),
    mappedIds         = new Set(),
  ) => {
    if (!viewer || !attributeResults.length) return;

    viewer.clearThemingColors(viewer.model);

    let goldCount = 0, cyanCount = 0, purpleCount = 0, darkPinkCount = 0;

    goldDbIdsRef.current.forEach((dbId) => {
      const assetIds  = getAssetIdsFromDbId(dbId); // Set<assetId>
      const isGrouped = assetIds.size > 1;

      const checkedInGroup = [...assetIds].filter((id) => checkedAssetIdSet.has(id));

      // 1. Gold — nothing checked in this node
      if (checkedInGroup.length === 0) {
        viewer.setThemingColor(dbId, COLOR_SELECTABLE_ASSET(), viewer.model, true);
        goldCount++;
        return;
      }

      // 2. Orange — grouped attribute, only some IDs checked
      if (isGrouped && checkedInGroup.length < assetIds.size) {
        viewer.setThemingColor(dbId, COLOR_GROUPED_PARTIAL_DARK_PINK(), viewer.model, true);
        darkPinkCount++;
        return;
      }

      // 3. Cyan / Purple — fully checked
      const hasMappedChecked = checkedInGroup.some((id) => mappedIds.has(id));
      if (hasMappedChecked) {
        viewer.setThemingColor(dbId, COLOR_MAPPED_CHECKED(), viewer.model, true);
        purpleCount++;
      } else {
        viewer.setThemingColor(dbId, COLOR_NON_MAPPED_CHECKED(), viewer.model, true);
        cyanCount++;
      }
    });

   log(
    `[applyIdListColors] gold: ${goldCount}, cyan: ${cyanCount}, ` +
    `purple: ${purpleCount}, dark-pink (partial group): ${darkPinkCount}`,
  );
  }, [getAssetIdsFromDbId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────
  // highlightMultipleAssets
  // ─────────────────────────────────────────────────────────────
  const highlightMultipleAssets = useCallback((assetIds, shouldFitView = true) => {
    const viewer = viewerRef.current;
    if (!viewer || !allAttributesRef.current.length) return;

    const dbIds = assetIds
      .map((id) => resolveDbId(id))
      .filter((dbId) => dbId !== undefined && goldDbIdsRef.current.has(dbId));

    if (!dbIds.length) return;

    applyIdListColors(
      viewer,
      allAttributesRef.current,
      highlightIdList,
      new Set(assetIds),
      mappedAssetIdsRef.current,
    );

    viewer.select(dbIds);
    if (shouldFitView) viewer.fitToView(dbIds);

    log(`[highlightMultipleAssets] Highlighted ${dbIds.length} assets`);
  }, [resolveDbId, highlightIdList, applyIdListColors]);

  // ─────────────────────────────────────────────────────────────
  // loadAndApplyAllAttributes
  // Builds all asset maps from the viewer model in a single pass,
  // then seeds the initial color state.
  // ─────────────────────────────────────────────────────────────
  const loadAndApplyAllAttributes = useCallback((viewer) => {
    const model = viewer.model;
    if (!model) {
      console.warn('[loadAndApplyAllAttributes] No model — aborting');
      return;
    }

    model.getObjectTree((instanceTree) => {
      const leafDbIds = getLeafDbIds(instanceTree);
      log('[loadAndApplyAllAttributes] leaf dbIds count:', leafDbIds.length);

      if (!leafDbIds.length) {
        console.warn('[loadAndApplyAllAttributes] No leaf components found');
        return;
      }

      model.getBulkProperties(
        leafDbIds,
        { propFilter: undefined },
        (results) => {
          allAttributesRef.current = results;

          // ── Build all maps in a single pass ───────────────────
          const assetIdToDbIdMap    = new Map();
          const dbIdToAssetIdsMap   = new Map(); // reverse one-to-many
          const assetIdToNameMap    = new Map();
          const assetIdToCalloutMap = new Map();
          const validDbIds          = new Set();
          const goldDbIds           = new Set();
          const newGoldAssetIds     = new Set();

          results.forEach(({ dbId, properties }) => {
            // Numeric fallback so dbId strings always resolve
            assetIdToDbIdMap.set(String(dbId), dbId);

            const ipsProps = properties.filter((p) =>
              p.displayName.includes(IPS_ASSET_ID_PROP),
            );
            if (!ipsProps.length) return;

            validDbIds.add(dbId);
            goldDbIds.add(dbId);

            ipsProps.forEach((ipsProp) => {
              const ipsAssetId = String(ipsProp.displayValue ?? '').trim();
              if (!ipsAssetId) return;

              assetIdToDbIdMap.set(ipsAssetId, dbId);
              newGoldAssetIds.add(ipsAssetId);

              // ── Build reverse one-to-many map ──────────────────
              if (!dbIdToAssetIdsMap.has(dbId)) dbIdToAssetIdsMap.set(dbId, new Set());
              dbIdToAssetIdsMap.get(dbId).add(ipsAssetId);

              const uniqueRowConcatProp = properties.find(
                (p) => p.displayName === 'UniqueRowConcat',
              );
              const zNameProp = properties.find(
                (p) => ['Z_NAME', 'OL_NAME'].includes(p.attributeName) ||
                        ['Z_NAME', 'OL_NAME'].includes(p.displayName),
              );
              assetIdToNameMap.set(
                ipsAssetId,
                uniqueRowConcatProp?.displayValue || zNameProp?.displayValue || '',
              );

              const calloutProp = properties.find((p) => p.displayName === 'CalloutNumber');
              assetIdToCalloutMap.set(ipsAssetId, calloutProp?.displayValue ?? '');
            });
          });

          // ── Commit refs ───────────────────────────────────────
          assetIdToDbIdRef.current          = assetIdToDbIdMap;
          dbIdToAssetIdsRef.current         = dbIdToAssetIdsMap;
          validDbIdsRef.current             = validDbIds;
          goldDbIdsRef.current              = goldDbIds;
          assetIdToNameRef.current          = assetIdToNameMap;
          assetIdToCalloutNumberRef.current = assetIdToCalloutMap;

          // ── Build mapped asset ID set ─────────────────────────
          const mappingAssetIds = (preEFormMappings ?? [])
            .flatMap((m) => (m.assets ?? []).map((a) => String(a.ipsAssetId ?? '').trim()))
            .filter(Boolean);

          const newMappedAssetIds = new Set(
            mappingAssetIds.filter((id) => newGoldAssetIds.has(id)),
          );

          mappedAssetIdsRef.current = newMappedAssetIds;
          setGoldAssetIds(newGoldAssetIds);
          setMappedAssetIds(newMappedAssetIds);

          // ── Diagnostics ───────────────────────────────────────
          const unmatched = mappingAssetIds.filter((id) => !newGoldAssetIds.has(id));
          if (unmatched.length) {
            console.warn(
              `[Diagnostic] ${unmatched.length} preEFormMapping assets NOT in viewer:`,
              unmatched,
            );
          } else if (mappingAssetIds.length > 0) {
            log('[Diagnostic] All preEFormMappings assets found in viewer ✅');
          }

          log(
            `[loadAndApplyAllAttributes] ${goldDbIds.size} nodes, ` +
            `${newGoldAssetIds.size} unique IPS IDs ` +
            `(${newMappedAssetIds.size} mapped, ${newGoldAssetIds.size - newMappedAssetIds.size} non-mapped)`,
          );

          // ── Seed initial colors ───────────────────────────────
          applyIdListColors(viewer, results, highlightIdList, new Set(checkedAssetIds), newMappedAssetIds);
        },
        (err) => console.error('[Viewer] getBulkProperties error:', err),
      );
    });
  }, [highlightIdList, preEFormMappings, checkedAssetIds, applyIdListColors]);

  // Keep ref current for stale-closure callbacks (e.g. handleViewerReady)
  useEffect(() => {
    loadAndApplyAllAttributesRef.current = loadAndApplyAllAttributes;
  }, [loadAndApplyAllAttributes]);

  // ─────────────────────────────────────────────────────────────
  // handleNativeSelectionChanged
  // Read-only mode blocks all selections. Otherwise gates to
  // gold nodes only and queues auto-check if enabled.
  // ─────────────────────────────────────────────────────────────
  const handleNativeSelectionChanged = useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    if (!saveVisibleTextRef.current) {
      viewer.clearSelection();
      return;
    }

    const selection = viewer.getAggregateSelection();
    const dbIds     = selection.flatMap((s) => s.selection);
    const goldOnly  = dbIds.filter((dbId) => goldDbIdsRef.current.has(dbId));

    if (!goldOnly.length) {
      viewer.clearSelection();
      return;
    }

    // Strip non-gold nodes from mixed selections
    if (goldOnly.length !== dbIds.length) viewer.select(goldOnly);

    if (autoCheckOnViewerSelection && !isUpdatingFromViewerRef.current) {
      setViewerSelectedDbIds(goldOnly);
    }
  }, [autoCheckOnViewerSelection, saveVisibleTextRef]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────
  // handleViewerReady
  // ─────────────────────────────────────────────────────────────
  const handleViewerReady = useCallback((viewerInstance) => {
    viewerRef.current = viewerInstance;

    viewerInstance.addEventListener(
      window.Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT,
      () => loadAndApplyAllAttributesRef.current?.(viewerInstance),
      { once: true },
    );

    viewerInstance.addEventListener(
      window.Autodesk.Viewing.AGGREGATE_SELECTION_CHANGED_EVENT,
      handleNativeSelectionChanged,
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-apply colors when highlight list or checked set changes
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !allAttributesRef.current.length) return;
    applyIdListColors(
      viewer,
      allAttributesRef.current,
      highlightIdList,
      new Set(checkedAssetIds),
      mappedAssetIds,
    );
  }, [highlightIdList, applyIdListColors, checkedAssetIds, mappedAssetIds]);

  return {
    // Refs
    viewerRef,
    allAttributesRef,
    assetIdToDbIdRef,
    assetIdToNameRef,
    assetIdToCalloutNumberRef,
    goldDbIdsRef,
    // State
    goldAssetIds,
    mappedAssetIds,
    hasPartialGroups,
    viewerSelectedDbIds,
    setViewerSelectedDbIds,
    // Guards
    isUpdatingFromViewerRef,
    // Handlers
    handleViewerReady,
    // Utilities
    applyIdListColors,
    resolveCheckedDbIds,
    resolveDbId,
    getAssetIdsFromDbId,
    highlightMultipleAssets,
  };
};

export default useViewerModel;